"use client"

import type { MapNode } from "@/types/map.types"

interface SpaceMapNodeProps {
  node: MapNode
  isAvailable: boolean
  isSelected: boolean
  isCurrent: boolean
  isVisited: boolean
  onClick: () => void
  zoom?: number
}

// Get the image path for a node based on its type and state
function getNodeImage(node: MapNode, isVisited: boolean): string {
  if (isVisited) {
    // Completed nodes use the same image but will be greyed out via CSS
    return getNodeTypeImage(node)
  }
  return getNodeTypeImage(node)
}

// Get the image for a node's type
function getNodeTypeImage(node: MapNode): string {
  if (node.type === "boss") {
    return "/images/starmap/starmap_node_boss.png"
  }
  if (node.type === "event") {
    // Event nodes show mystery icon until revealed
    if (!node.eventRevealed) {
      return "/images/starmap/starmap_node_specialevent.png"
    }
    // Once revealed, show the actual event type
    if (node.eventType === "shop") {
      return "/images/starmap/starmap_node_shop.png"
    }
    if (node.eventType === "pelf") {
      return "/images/starmap/starmap_node_pelf.png"
    }
    // Fallback to special event if eventType is somehow missing
    return "/images/starmap/starmap_node_specialevent.png"
  }
  // Regular combat node
  return "/images/starmap/starmap_node_combat.png"
}

// Get glow color based on node type
function getGlowColor(node: MapNode): string {
  if (node.type === "boss") return "bg-red-500"
  if (node.type === "event") return "bg-purple-500"
  return "bg-cyan-500"
}

export function SpaceMapNode({
  node,
  isAvailable,
  isSelected,
  isCurrent,
  isVisited,
  onClick,
  zoom = 1,
}: SpaceMapNodeProps) {
  const baseSize = 56
  const size = baseSize * zoom
  const isInteractive = isAvailable && !isCurrent
  const isLocked = !isAvailable && !isCurrent && !isVisited

  const nodeImage = getNodeImage(node, isVisited)
  const glowColor = getGlowColor(node)

  return (
    <button
      onClick={isInteractive ? onClick : undefined}
      disabled={!isInteractive}
      className={`
        absolute flex items-center justify-center
        transition-all duration-200
        ${isInteractive ? "cursor-pointer hover:scale-110 active:scale-95" : "cursor-default"}
        ${isLocked ? "opacity-40" : "opacity-100"}
      `}
      style={{
        left: node.position.x * zoom - size / 2,
        top: node.position.y * zoom - size / 2,
        width: size,
        height: size,
        touchAction: "manipulation",
      }}
    >
      {/* Glow effect for available nodes */}
      {isAvailable && !isVisited && (
        <div
          className={`
            absolute inset-0 rounded-full
            ${glowColor}
            ${isSelected ? "opacity-60" : "opacity-40"}
            animate-pulse
          `}
          style={{
            filter: "blur(12px)",
            transform: "scale(1.2)",
          }}
        />
      )}

      {/* Current position indicator - pulsing border */}
      {isCurrent && (
        <div
          className="absolute inset-0 rounded-full border-white animate-pulse"
          style={{
            transform: "scale(1.3)",
            borderWidth: `${Math.max(2, 2 * zoom)}px`,
          }}
        />
      )}

      {/* Node image */}
      <div
        className={`
          relative w-full h-full
          flex items-center justify-center
          ${isSelected ? "ring-2 ring-white ring-offset-2 ring-offset-[#0a0a1a] rounded-full" : ""}
        `}
      >
        <img
          src={nodeImage}
          alt={node.type}
          className={`
            w-full h-full object-contain
            ${isVisited ? "grayscale brightness-50" : ""}
            ${isLocked ? "grayscale brightness-50" : ""}
          `}
          style={{
            imageRendering: "pixelated",
          }}
        />

        {/* Checkmark overlay for completed nodes */}
        {isVisited && (
          <div
            className="absolute inset-0 flex items-center justify-center"
            style={{
              filter: "drop-shadow(0 0 3px rgba(0, 0, 0, 0.9)) drop-shadow(0 0 6px rgba(0, 0, 0, 0.7))",
            }}
          >
            <svg
              width={36 * zoom}
              height={36 * zoom}
              viewBox="0 0 24 24"
              fill="none"
            >
              <path
                d="M5 13l4 4L19 7"
                stroke="#22c55e"
                strokeWidth="3"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </div>
        )}

        {/* Current position marker */}
        {isCurrent && (
          <div
            className="absolute bg-white rounded-full flex items-center justify-center"
            style={{
              bottom: -4 * zoom,
              right: -4 * zoom,
              width: 16 * zoom,
              height: 16 * zoom,
            }}
          >
            <div
              className="bg-cyan-500 rounded-full"
              style={{
                width: 8 * zoom,
                height: 8 * zoom,
              }}
            />
          </div>
        )}
      </div>
    </button>
  )
}
